/*
 * halal-label/wizard.js — "Izin apa yang saya perlukan?" as a pure function.
 *
 * Every verdict names the clause it stands on; the page prints the texts
 * (lang file) for the codes returned here. Sources:
 *   PerBPOM 4/2024 Pasal 3(3), 5 — who needs SPP-IRT (P-IRT), who does not;
 *   PerBPOM 27/2025 Lampiran C.6 — KBLI open to SPP-IRT: room temperature
 *     >= 7 days, drinks only as powder, no frozen/canned noodles…;
 *   PerBPOM 31/2018 Pasal 36 — BPOM RI MD (domestic), ML (import);
 *   PP 42/2024 Pasal 2, 110, 160, 170-171; Peraturan BPJPH 3/2026 — halal
 *     certificate vs "keterangan tidak halal";
 *   PerBPOM 10/2026 Pasal 2-4, 7, 27, 31-32 — ING and Nutri-Level.
 *
 * ⚠️ The most expensive mistake on the market: "no certificate yet, so I
 * print NON HALAL". NON HALAL is for products FROM haram materials or made
 * in a process outside SJPH (Pasal 110 PP 42/2024; Pasal 3-4 Peraturan
 * BPJPH 3/2026). A halal-material product without a certificate is in breach
 * of the duty to certify — NON HALAL does not cure that, it mislabels it.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.HalalWizard = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* a = answers:
     *   origin:   'domestic' | 'import'
     *   scale:    'mikro' | 'kecil' | 'besar'      (menengah/besar together)
     *   place:    'home' | 'factory'               (residence, manual/semi-auto?)
     *   storage:  'ambient' | 'chilled' | 'frozen'
     *   shelf7:   true if it keeps >= 7 days at that storage
     *   form:     'solid' | 'drink_powder' | 'drink_liquid' | 'concentrate'
     *   special:  'none' | 'canned' | 'dairy' | 'baby' | 'alcohol' | 'water' | 'exempt_ing'
     *   readyToEat: sold ready to eat on the spot (siap saji)
     *   haram:    'none' | 'pork' | 'other' | 'process'
     *   porkFacility: (with 'process') shares equipment with pork materials
     *   sh:       'yes' | 'applying' | 'no'
     *   simple:   ingredients certainly halal + simple process (self declare)
     */
    function decide(a) {
        a = a || {};
        var out = { permit: permit(a), halal: halal(a), ing: ing(a), nutri: nutri(a) };
        // What the label editor should start from.
        out.label = {
            permit: out.permit.verdict,
            halalMark: out.halal.mark,
            ingSource: out.ing.source,
        };
        return out;
    }

    function permit(a) {
        var r = [];
        if (a.origin === 'import') {
            return { verdict: 'ml', reasons: ['import'], refs: ['PerBPOM 31/2018 Pasal 36(2)'] };
        }
        if (a.readyToEat) {
            return { verdict: 'none', reasons: ['ready_to_eat'], refs: ['PerBPOM 4/2024 Pasal 3(3) huruf b'] };
        }
        if (a.shelf7 === false) {
            return { verdict: 'none', reasons: ['shelf_under_7'], refs: ['PerBPOM 4/2024 Pasal 3(3) huruf a'] };
        }
        if (a.storage === 'frozen') r.push('frozen');
        if (a.storage === 'chilled') r.push('chilled');
        if (a.special === 'canned') r.push('canned');
        if (a.form === 'drink_liquid' || a.form === 'concentrate') r.push('liquid_drink');
        if (a.special === 'dairy') r.push('dairy');
        if (a.special === 'baby') r.push('baby');
        if (a.special === 'alcohol') r.push('alcohol');
        if (a.special === 'water') r.push('water');
        if (a.place === 'factory') r.push('factory');
        if (r.length) {
            return { verdict: 'md', reasons: r, refs: ['PerBPOM 27/2025 Lampiran C.6', 'PerBPOM 4/2024 Pasal 5', 'PerBPOM 31/2018 Pasal 36(1)'] };
        }
        return { verdict: 'pirt', reasons: ['pirt_fits'], refs: ['PerBPOM 4/2024 Pasal 3-5, Lampiran I-II', 'PerBPOM 27/2025 Lampiran C.6'] };
    }

    function halal(a) {
        if (a.haram === 'pork') {
            return { verdict: 'not_halal', mark: 'babi', reasons: ['pork'],
                refs: ['PP 42/2024 Pasal 110', 'Peraturan BPJPH 3/2026 Pasal 21 huruf a, Lampiran A.1', 'PerBPOM 31/2018 Pasal 40'] };
        }
        if (a.haram === 'other') {
            return { verdict: 'not_halal', mark: 'nonhalal', reasons: ['haram_material'],
                refs: ['PP 42/2024 Pasal 110', 'Peraturan BPJPH 3/2026 Pasal 5-19, Lampiran A.2'] };
        }
        if (a.haram === 'process') {
            var res = { verdict: 'not_halal', mark: 'nonhalal', reasons: ['haram_process'],
                refs: ['Peraturan BPJPH 3/2026 Pasal 20, Lampiran A.3'] };
            if (a.porkFacility) {
                res.bpomShared = true;
                res.refs.push('PerBPOM 31/2018 Pasal 41-42');
            }
            return res;
        }
        // Halal materials and process: the product must be CERTIFIED.
        var due = a.origin === 'import' ? 'import'
            : (a.scale === 'besar' ? '2024-10-17' : '2026-10-17');
        var res2 = {
            verdict: 'certify', due: due,
            refs: a.origin === 'import'
                ? ['UU 33/2014 Pasal 4', 'PP 42/2024 Pasal 160 ayat (3)']
                : ['UU 33/2014 Pasal 4', 'PP 42/2024 Pasal 160 ayat (1)-(2)'],
        };
        if (a.sh === 'yes') {
            res2.mark = 'label';
            res2.reasons = ['has_sh'];
            res2.refs.push('PP 42/2024 Pasal 107');
        } else {
            res2.mark = 'none';
            // the line people get wrong
            res2.reasons = ['no_sh_not_nonhalal', a.sh === 'applying' ? 'applying' : 'no_sh'];
            res2.refs.push('PP 42/2024 Pasal 170-171');
            res2.path = (a.origin !== 'import' && (a.scale === 'mikro' || a.scale === 'kecil') && a.simple)
                ? 'self_declare' : 'reguler';
        }
        return res2;
    }

    function ing(a) {
        if (a.special === 'alcohol') {
            return { verdict: 'forbidden', refs: ['PerBPOM 10/2026 Pasal 4'] };
        }
        if (a.special === 'exempt_ing' || a.special === 'water') {
            return { verdict: 'exempt', refs: ['PerBPOM 10/2026 Pasal 3'] };
        }
        var source = a.origin === 'import' || a.scale !== 'mikro' ? 'lab' : 'kepka_or_lab';
        return {
            verdict: 'required', source: source,
            // 24 months from the regulation (diundangkan June 2026) for
            // products already permitted; new permits follow it now.
            transition: '2028-06',
            refs: source === 'lab'
                ? ['PerBPOM 10/2026 Pasal 2, 6, 31']
                : ['PerBPOM 10/2026 Pasal 2, 7, 31', 'Kepka BPOM 265/2026'],
        };
    }

    function nutri(a) {
        if (a.form === 'drink_powder' || a.form === 'drink_liquid' || a.form === 'concentrate') {
            if (a.special === 'baby') return { verdict: 'forbidden', refs: ['PerBPOM 10/2026 Pasal 27(2)'] };
            return { verdict: 'required_later', from: '2028-06', refs: ['PerBPOM 10/2026 Pasal 27, 29, 32'] };
        }
        return { verdict: 'none' };
    }

    return { decide: decide };
});
